import FormHeader from "@/components/FormHeader";
import Modal from "@/components/Modal";
import SkeletonCard from "@/components/SkeletonCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import useApiBaseUrl from "@/hooks/useApiBaseUrl";
import useDataPendaftaran from "@/hooks/useDataPendaftaran";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import api from "@/lib/axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertTriangle,
  CheckCircle2,
  FileCheck,
  FileDown,
  Loader2,
  Lock,
} from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";

const validasiPendaftaran = async (id_data_pendaftaran: string) => {
  const response = await api.patch(
    "/siswa/penerimaan/smbjm/pendaftaran/validasi",
    { id_data_pendaftaran }
  );
  return response.data;
};

export default function KartuPeserta({
  idDataPendaftaran,
}: {
  idDataPendaftaran: string;
}) {
  const { apiBaseUrl } = useApiBaseUrl();
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });
  const { isPendingPeriode, timePassedActive } = useTimePeriode();
  const { isValidPendaftaran, isPendingDataSiswa } = useDataPendaftaran();

  // // Menggunakan state lokal atau query untuk status download agar lebih reaktif daripada localStorage langsung saat render
  // const [isDownloaded, setIsDownloaded] = useState(
  //   typeof window !== "undefined"
  //     ? localStorage.getItem("is_download_kartu_peserta_pasca") === "true"
  //     : false
  // );

  const [showConfirmation, setShowConfirmation] = useState(false);
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: validasiPendaftaran,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["dataSiswa"] });
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      queryClient.invalidateQueries({ queryKey: ["dataBiodata"] });
      setShowConfirmation(false);
      toast.success(data.message || "Validasi berhasil.");
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const errorMessage =
        error?.response?.data?.message || "Terjadi kesalahan saat validasi.";
      toast.error(errorMessage);
    },
  });

  const handleValidasiPendaftaran = (e: React.FormEvent) => {
    e.preventDefault();
    mutate(idDataPendaftaran);
  };

  const handleDownload = () => {
    // localStorage.setItem("is_download_kartu_peserta_pasca", "true");
    // setIsDownloaded(true);
    window.open(
      `${apiBaseUrl}/siswa/penerimaan/smbjm/kartu-peserta/pascasarjana?id_sys_periode=${sessionPeriode?.id_sys_periode}`,
      "_blank"
    );
  };

  if (isPendingDataSiswa) {
    return (
      <div className="grid md:grid-cols-2 gap-6">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  const isExpired = timePassedActive?.waktu_selesai_daftar_valid_selesai;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h1 className="font-bold text-2xl tracking-tight">
          Finalisasi Pendaftaran
        </h1>
        <p className="text-muted-foreground text-sm">
          Lakukan validasi data untuk menyelesaikan pendaftaran dan mengunduh
          Kartu Peserta.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 items-stretch">
        {/* CARD 1: VALIDASI */}
        <Card
          className={`relative flex flex-col justify-between border-t-4 shadow-sm transition-all ${
            isValidPendaftaran
              ? "border-t-green-500 bg-green-50/10"
              : "border-t-blue-500"
          }`}
        >
          <CardHeader>
            <div className="flex justify-between items-start">
              <div
                className={`p-3 rounded-xl mb-4 w-fit ${
                  isValidPendaftaran
                    ? "bg-green-100 text-green-600"
                    : "bg-blue-100 text-blue-600"
                }`}
              >
                <FileCheck size={28} />
              </div>
              <Badge
                variant={isValidPendaftaran ? "default" : "outline"}
                className={
                  isValidPendaftaran ? "bg-green-600 hover:bg-green-700" : ""
                }
              >
                Langkah 1
              </Badge>
            </div>
            <CardTitle className="text-xl">Validasi Data</CardTitle>
            <CardDescription className="mt-2 text-sm leading-relaxed">
              Verifikasi seluruh data yang telah diinput. Data yang sudah
              divalidasi akan dikunci permanen dan{" "}
              <strong>tidak dapat diubah kembali</strong>.
            </CardDescription>
          </CardHeader>

          <CardFooter>
            {isValidPendaftaran ? (
              <Button
                variant="outline"
                className="w-full gap-2 text-green-600 border-green-200 bg-green-50 hover:bg-green-100 cursor-default"
              >
                <CheckCircle2 size={18} /> Sudah Divalidasi
              </Button>
            ) : (
              <Button
                onClick={() => setShowConfirmation(true)}
                disabled={isPendingPeriode || isExpired || false}
                className="w-full gap-2 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isExpired ? (
                  <>
                    <Lock size={18} /> Pendaftaran Ditutup{" "}
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={18} /> Validasi Sekarang{" "}
                  </>
                )}
              </Button>
            )}
          </CardFooter>
        </Card>

        {/* CARD 2: DOWNLOAD KARTU */}
        <Card
          className={`relative flex flex-col justify-between border-t-4 shadow-sm transition-all dark:bg-slate-900 ${
            !isValidPendaftaran
              ? "border-t-slate-200 bg-slate-50 opacity-80 dark:border-t-slate-700 dark:bg-slate-900/50"
              : "border-t-rose-500 dark:border-t-rose-600 bg-white dark:bg-slate-900"
          }`}
        >
          {/* OVERLAY LOCK (Jika belum validasi) */}
          {!isValidPendaftaran && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-black/60 backdrop-blur-[1px] rounded-xl">
              <div className="bg-white dark:bg-slate-800 px-4 py-2 rounded-full shadow-md border dark:border-slate-700 text-sm font-medium flex items-center gap-2 text-muted-foreground dark:text-slate-300">
                <Lock size={14} /> Selesaikan Langkah 1 Dulu
              </div>
            </div>
          )}

          <CardHeader>
            <div className="flex justify-between items-start">
              {/* ICON CONTAINER */}
              <div
                className={`p-3 rounded-xl mb-4 w-fit ${
                  isValidPendaftaran
                    ? "bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400"
                    : "bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                }`}
              >
                <FileDown size={28} />
              </div>

              {/* BADGE LANGKAH */}
              <Badge
                variant={isValidPendaftaran ? "secondary" : "outline"}
                className="dark:border-slate-700 dark:text-slate-300"
              >
                Langkah 2
              </Badge>
            </div>

            <CardTitle className="text-xl dark:text-white">
              Kartu Peserta
            </CardTitle>
            <CardDescription className="mt-2 text-sm leading-relaxed dark:text-slate-400">
              Unduh dan cetak Kartu Peserta Ujian. Dokumen ini wajib dibawa saat
              pelaksanaan ujian seleksi masuk.
            </CardDescription>
          </CardHeader>

          <CardFooter>
            <Button
              variant={isValidPendaftaran ? "default" : "secondary"}
              className={`w-full gap-2 transition-colors ${
                isValidPendaftaran
                  ? "bg-rose-600 hover:bg-rose-700 text-white dark:bg-rose-600 dark:hover:bg-rose-700"
                  : "dark:bg-slate-800 dark:text-slate-400"
              }`}
              onClick={handleDownload}
              disabled={!isValidPendaftaran}
            >
              <FileDown size={18} />
              Download
            </Button>
          </CardFooter>
        </Card>
      </div>

      {/* MODAL KONFIRMASI */}
      <Modal show={showConfirmation} onClose={() => setShowConfirmation(false)}>
        <form onSubmit={handleValidasiPendaftaran}>
          <FormHeader
            title="Konfirmasi Validasi"
            icon={<AlertTriangle className="text-amber-500" />}
            description="Tindakan ini bersifat permanen. Apakah Anda yakin data sudah benar?"
          />

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4 text-sm text-amber-800">
            <ul className="list-disc pl-4 space-y-1">
              <li>Data biodata tidak bisa diedit lagi.</li>
              <li>Berkas yang diunggah tidak bisa diganti.</li>
              <li>Anda akan mendapatkan Kartu Peserta setelah ini.</li>
            </ul>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <Button
              type="button"
              variant="ghost"
              disabled={isPending}
              onClick={() => setShowConfirmation(false)}
            >
              Periksa Lagi
            </Button>
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2 min-w-[120px]"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Proses...
                </>
              ) : (
                <>
                  <CheckCircle2 size={18} /> Ya, Validasi
                </>
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
